import {
  ConsistencyScoreResultResponse,
  ConsistencyScoreControllerService,
  ConsistencyScoreGoalResponse,
  RequirementFulfillmentResponse,
  GoalRequirementResponse
} from "@9amhealth/openapi";
import { Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import { loadingState } from "src/state/state";
import { TaskResponseKnown } from "../TaskManagementBloc/TaskManagementBloc";
import translate from "src/lib/translate";
import { dateLocalString } from "src/lib/date";
import { featureFlags } from "src/lib/featureFlags";

export const ConsistencyScoreStatus = ConsistencyScoreResultResponse.status;
export type ConsistencyScoreType = ConsistencyScoreResultResponse.type;
export const RequirementFulfillmentType = RequirementFulfillmentResponse.type;

export type ConsistencyScoreGoalEngagementType =
  ConsistencyScoreGoalResponse.engagementGoalType;
export type RequirementFulfillmentGoalType = GoalRequirementResponse.goalType;

export interface ConsistencyScoreGoal {
  engagementGoalType: ConsistencyScoreGoalEngagementType;
  title: string;
  count: number;
  maxCount: number;
}

export interface GoalRequirement {
  goalType: RequirementFulfillmentGoalType;
  minCount: number;
}

export interface RequirementFulfillment {
  type: keyof typeof RequirementFulfillmentType;
  threshold: number;
  goalRequirements: GoalRequirement[];
  fulfilled?: boolean;
}

export interface ConsistencyScore {
  score: number;
  status: keyof typeof ConsistencyScoreStatus;
  goals: ConsistencyScoreGoal[];
  type: ConsistencyScoreType;
  requirementFulfillments: RequirementFulfillment[];
}

export interface ConsistencyScoreState {
  data?: ConsistencyScore;
  loading: boolean;
  error: string | null;
  glp1Requirement?: RequirementFulfillment;
  isGlp1RequirementReached: boolean;
}

export default class ConsistencyScoreCubit extends Cubit<ConsistencyScoreState> {
  static keepAlive = true;
  constructor() {
    super({
      data: undefined,
      loading: false,
      error: null,
      glp1Requirement: undefined,
      isGlp1RequirementReached: false
    });

    void this.fetchConsistencyScore();
  }

  public readonly fetchConsistencyScore = async (): Promise<void> => {
    if (!featureFlags.consistencyScoreEnabled) {
      return;
    }

    loadingState.start(LoadingKey.consistencyScore);

    this.patch({
      loading: true,
      error: null
    });

    try {
      const { data } =
        await ConsistencyScoreControllerService.getConsistencyScore();

      const parsedData = this.parseConsistencyScore(data);

      this.patch({
        data: parsedData,
        loading: false,
        error: null
      });

      this.setRequirementState(parsedData);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch consistency score";
      this.patch({
        loading: false,
        error: errorMessage
      });
      reportErrorSentry(error);
    }

    loadingState.finish(LoadingKey.consistencyScore);
  };

  public readonly clearData = (): void => {
    this.patch({
      data: undefined,
      loading: false,
      error: null,
      glp1Requirement: undefined,
      isGlp1RequirementReached: false
    });
  };

  public readonly refresh = (): void => {
    void this.fetchConsistencyScore();
  };

  private readonly parseGoalRequirement = (
    goalRequirement: GoalRequirementResponse
  ): GoalRequirement => {
    return {
      goalType: goalRequirement.goalType,
      minCount: goalRequirement.minCount
    };
  };

  private readonly parseConsistencyScoreGoal = (
    goal: ConsistencyScoreGoalResponse
  ): ConsistencyScoreGoal => {
    return {
      engagementGoalType: goal.engagementGoalType,
      title: goal.title,
      count: goal.count,
      maxCount: goal.maxCount
    };
  };

  private readonly parseRequirementFulfillment = (
    fulfillment: RequirementFulfillmentResponse
  ): RequirementFulfillment => {
    return {
      type: fulfillment.type,
      threshold: fulfillment.threshold,
      fulfilled: fulfillment.fulfilled,
      goalRequirements: fulfillment.goalRequirements.map(
        this.parseGoalRequirement
      )
    };
  };
  private readonly parseConsistencyScore = (
    response: ConsistencyScoreResultResponse
  ): ConsistencyScore => {
    return {
      score: response.score,
      requirementFulfillments: response.requirementFulfillments.map(
        this.parseRequirementFulfillment
      ),
      type: response.type,
      status: response.status,
      goals: response.goals.map(this.parseConsistencyScoreGoal)
    };
  };

  private readonly setRequirementState = (data?: ConsistencyScore) => {
    const glp1Requirement = data?.requirementFulfillments.find(
      (req) => req.type === RequirementFulfillmentType.GLP1_REFILL
    );

    const isGlp1RequirementReached =
      Boolean(glp1Requirement?.threshold) &&
      typeof data?.score === "number" &&
      data.score >= (glp1Requirement?.threshold ?? 0);

    this.patch({
      glp1Requirement,
      isGlp1RequirementReached
    });
  };

  private readonly isRefillSurveyDateReached = (
    refillSurveyTask?: TaskResponseKnown
  ): boolean => {
    if (!refillSurveyTask) {
      return false;
    }

    const now = new Date();
    const availableFrom = refillSurveyTask.availableFrom
      ? new Date(refillSurveyTask.availableFrom)
      : null;
    return !availableFrom || now >= availableFrom;
  };

  // Determine the task description based on status and available date
  public readonly getGlp1RefillTaskDescription = (
    refillSurveyTask?: TaskResponseKnown
  ): string => {
    if (!refillSurveyTask) {
      return "";
    }

    const { glp1Requirement, data } = this.state;
    const isAvailableDateReached =
      this.isRefillSurveyDateReached(refillSurveyTask);

    if (refillSurveyTask.status === "LOCKED") {
      if (isAvailableDateReached) {
        // Locked because of consistency score not reached
        // Calculate how many more points are needed to unlock
        if (glp1Requirement?.threshold && data?.score !== undefined) {
          const remainingPoints = glp1Requirement.threshold - data.score;
          if (remainingPoints > 0) {
            return translate(
              "prepareRefillDialog.refillSurvey.description.points",
              {
                points: remainingPoints
              }
            );
          } else {
            return "";
          }
        }

        // Fallback if calculation fails
        return translate(
          "prepareRefillDialog.refillSurvey.description.pointsFallback"
        );
      } else {
        // Locked because not yet available
        const formattedDate = dateLocalString(refillSurveyTask.availableFrom, {
          dateStyle: "medium",
          timeStyle: "short"
        });
        return translate(
          "prepareRefillDialog.refillSurvey.description.unlocksAt",
          {
            date: formattedDate
          }
        );
      }
    }

    return "";
  };
}
